import React from 'react'
import styled, { css } from 'styled-components'
import { Icon } from '../../../../old/icon'
import { Box, Text } from '../../../core-components'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { AriaProps, OmitInternalProperties, PositioningProps } from '../../types'
import { useAnchorProps, useButtonProps } from '../hooks'
import { AnchorOnlyProps, ButtonOnlyProps } from '../types'
import { ThemeColors, ThemeShadows } from '../../../theme'
import {
  baseLinkButtonProps,
  BaseLinkButtonVariant,
  baseLinkButtonVariantProps,
} from '../base-button/BaseLinkButton'

type CommonProps = PositioningProps & {
  INTERNAL_endIcon?: React.ComponentType
  INTERNAL_endIconColor?: ThemeColors
  INTERNAL_nativeOnClick?: (event: React.MouseEvent) => void
  UNSAFE_hexColor?: string
  ariaProps?: AriaProps
  children: string | string[]
  'data-id'?: string
  'data-test-id'?: string
  isDisabled?: boolean
  startIcon?: React.ComponentType
  variant: BaseLinkButtonVariant
}

export type ButtonProps = CommonProps & ButtonOnlyProps

export type AnchorProps = CommonProps & AnchorOnlyProps

type Props = ButtonProps | AnchorProps

export const LinkButtonInternal = ({ children, ...props }: Props) => {
  if (props.as === 'a') {
    return <LinkButtonAsAnchor {...props}>{children}</LinkButtonAsAnchor>
  } else {
    return <LinkButtonAsButton {...props}>{children}</LinkButtonAsButton>
  }
}

type PublicProps = OmitInternalProperties<ButtonProps> | OmitInternalProperties<AnchorProps>

export const LinkButtonPublic = (props: PublicProps) => <LinkButtonInternal {...props} />

const LinkButtonAsButton = ({
  children,
  isDisabled,
  UNSAFE_hexColor,
  INTERNAL_nativeOnClick,
  INTERNAL_endIcon,
  variant,
  onClick,
  type,
  startIcon,
  'data-id': dataId,
  'data-test-id': dataTestId,
  ariaProps,
  as: _as,
  ...positioningProps
}: ButtonProps) => {
  validatePositioningProps(positioningProps, 'LinkButton')

  const { buttonProps, ref } = useButtonProps({
    isDisabled,
    onClick,
    type,
    INTERNAL_nativeOnClick,
    ...ariaProps,
  })
  const commonProps = getCommonProps(variant, UNSAFE_hexColor)
  const { color, iconColor } = baseLinkButtonVariantProps[variant]

  return (
    <StyledBox
      as='button'
      ref={ref}
      data-id={dataId}
      data-test-id={dataTestId}
      {...commonProps}
      {...positioningProps}
      {...buttonProps}
    >
      <Content
        color={color}
        startIcon={startIcon}
        startIconColor={iconColor}
        endIcon={INTERNAL_endIcon}
      >
        {children}
      </Content>
    </StyledBox>
  )
}

const LinkButtonAsAnchor = ({
  children,
  isDisabled,
  UNSAFE_hexColor,
  INTERNAL_nativeOnClick,
  INTERNAL_endIcon,
  INTERNAL_endIconColor,
  variant,
  onClick,
  target,
  href,
  rel,
  startIcon,
  'data-id': dataId,
  'data-test-id': dataTestId,
  as: _as,
  ariaProps,
  ...positioningProps
}: AnchorProps) => {
  validatePositioningProps(positioningProps, 'LinkButton')

  const { anchorProps, ref } = useAnchorProps({
    ...ariaProps,
    isDisabled,
    onClick,
    INTERNAL_nativeOnClick,
    href,
    rel,
    target,
  })

  const commonProps = getCommonProps(variant, UNSAFE_hexColor)
  const { color, iconColor } = baseLinkButtonVariantProps[variant]

  return (
    <StyledBox
      as='a'
      ref={ref}
      data-id={dataId}
      data-test-id={dataTestId}
      {...commonProps}
      {...positioningProps}
      {...anchorProps}
    >
      <Content
        color={color}
        startIconColor={iconColor}
        startIcon={startIcon}
        endIcon={INTERNAL_endIcon}
        endIconColor={INTERNAL_endIconColor}
      >
        {children}
      </Content>
    </StyledBox>
  )
}

const Content = ({
  children,
  color,
  startIcon,
  startIconColor,
  endIcon,
  endIconColor = 'fg/minor',
}: {
  children: string | string[]
  color: ThemeColors
  endIcon?: React.ComponentType
  endIconColor?: ThemeColors
  startIcon?: React.ComponentType
  startIconColor: ThemeColors
}) => {
  return (
    <Box display='flex' alignItems='center'>
      {startIcon && (
        <StyledStartIcon
          flexShrink={0}
          icon={startIcon}
          color={startIconColor}
          w={4}
          h={4}
          mr={2}
        />
      )}
      <Text display='block' pb='2px' textStyle='button' color={color}>
        {children}
      </Text>
      {endIcon && (
        <StyledEndIcon flexShrink={0} w={4} h={4} ml={2} color={endIconColor} icon={endIcon} />
      )}
    </Box>
  )
}

const StyledStartIcon = styled(Icon)``
const StyledEndIcon = styled(Icon)``

const StyledBox = styled(Box)<{
  UNSAFE_hexColor?: string
  hoverUnderlineShadow: ThemeShadows
  underlineShadow: ThemeShadows
}>`
  cursor: pointer;
  text-decoration: none;

  ${Text} {
    box-shadow: ${({ theme, underlineShadow }) => theme.shadows[underlineShadow]};
    transition: box-shadow 0.2s ease-in-out;
  }

  &[aria-expanded='true'] ${StyledEndIcon} {
    transform: rotate(180deg);
  }

  &[aria-expanded='true'],
  :hover {
    ${Text} {
      box-shadow: ${({ theme, hoverUnderlineShadow }) => theme.shadows[hoverUnderlineShadow]};
    }
  }

  ${({ UNSAFE_hexColor }) => css`
    ${StyledStartIcon} {
      color: ${UNSAFE_hexColor};
    }

    ${Text} {
      color: ${UNSAFE_hexColor};
      box-shadow: inset 0px -2px 0px ${UNSAFE_hexColor}1F;
    }

    :hover {
      ${Text} {
        box-shadow: inset 0px -2px 0px ${UNSAFE_hexColor}FF;
      }
    }
  `}
  :disabled,
  &[aria-disabled='true'] {
    cursor: default;

    ${StyledStartIcon},
    ${StyledEndIcon} {
      color: ${({ theme }) => theme.colors['fg/disabled']};
    }

    ${Text} {
      box-shadow: none;
      color: ${({ theme }) => theme.colors['fg/disabled']};
    }
  }

  :focus-visible {
    position: relative;
    outline: none;

    ${Text} {
      box-shadow: none;
    }
    ::after {
      content: '';
      position: absolute;
      inset: -4px;
      box-shadow: ${({ theme }) => theme.shadows['border/focus-indicator']};
    }
  }
`

const getCommonProps = (variant: Props['variant'], UNSAFE_hexColor?: string) => {
  const { hoverUnderlineShadow } = variantProps[variant]
  const { underlineShadow } = baseLinkButtonVariantProps[variant]

  return {
    ...baseLinkButtonProps,
    underlineShadow,
    hoverUnderlineShadow,
    UNSAFE_hexColor,
  } as const
}

const variantProps: Record<
  BaseLinkButtonVariant,
  {
    hoverUnderlineShadow: ThemeShadows
  }
> = {
  major: {
    hoverUnderlineShadow: 'border/link-underline-major-hover',
  },
  minor: {
    hoverUnderlineShadow: 'border/link-underline-minor-hover',
  },
  caution: {
    hoverUnderlineShadow: 'border/link-underline-caution-hover',
  },
  onColor: {
    hoverUnderlineShadow: 'border/link-underline-minor-on-color',
  },
  success: {
    hoverUnderlineShadow: 'border/link-underline-minor-hover',
  },
  warning: {
    hoverUnderlineShadow: 'border/link-underline-minor-hover',
  },
}
